import { useState, useEffect, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Login from './pages/Login';
import Pricing from './pages/Pricing';
import PhotoGeneration from './pages/PhotoGeneration';
import VideoGeneration from './pages/VideoGeneration';
import VideoTemplate from './pages/VideoTemplate';
import { Gallery } from './pages/Gallery';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import TakeItDownReport from './pages/TakeItDownReport';
import Partner from './pages/Partner';
import { RedSidebar, MobileHeader, MobileMenu } from './components/Sidebar';
import SignInPopup from './popups/SignInPopup';
import AccountPopup from './popups/AccountPopup';
import { logPageView, fetchUser } from '@genfun/core/api/auth';

function AppLayout({ children, token, user, setUser, setSignedIn, setToken, showLoginPopup, setShowLoginPopup }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    setUser(null);
    setToken(null);
    setSignedIn(false);
    localStorage.removeItem("igenfuntoken");
  };

  const refreshUser = useCallback(async () => {
    if (!token) return;
    const [u] = await fetchUser(token);
    if (u) setUser(u);
  }, [token, setUser]);

  return (
    <div className="appLayout">
      <MobileHeader
        user={user}
        credits={user?.credits || 0}
        onMenuClick={() => setMobileMenuOpen(true)}
        onLoginClick={() => navigate('/login')}
        onCreditsClick={() => setShowAccount(true)}
        onLogoClick={() => navigate('/video')}
      />
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        currentPath={location.pathname}
        onNavigate={(path) => navigate(path)}
        user={user}
        onSignOut={handleSignOut}
      />
      <RedSidebar
        token={token}
        user={user}
        setUser={setUser}
        setSignedIn={setSignedIn}
        setToken={setToken}
        onCreditsClick={() => setShowAccount(true)}
      />
      <main className="mainContent">
        {children}
      </main>
      <SignInPopup
        isOpen={showLoginPopup}
        setIsOpen={setShowLoginPopup}
        setToken={setToken}
      />
      <AccountPopup
        isOpen={showAccount}
        onClose={() => { setShowAccount(false); refreshUser(); }}
        token={token}
        user={user}
        refreshUser={refreshUser}
      />
    </div>
  );
}

// Read at import time, before React mounts. The "/" -> "/video" <Navigate> redirects
// from a mount effect, and child effects run before parent ones, so by the time App's
// effect runs the query string is already gone.
const initialRef = new URLSearchParams(window.location.search).get('ref');

function App() {
  const [token, setToken] = useState(() => localStorage.getItem("igenfuntoken"));
  const [user, setUser] = useState(null);
  const [signedIn, setSignedIn] = useState(!!token);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem("igenfuntoken", token);
    } else {
      localStorage.removeItem("igenfuntoken");
    }
  }, [token]);

  useEffect(() => {
    // Store referral code from URL in localStorage on first visit
    if (initialRef && !localStorage.getItem('referral_code')) {
      localStorage.setItem('referral_code', initialRef);
    }
    if (!token) {
      const ref = initialRef || localStorage.getItem('referral_code');
      logPageView(ref);
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <Routes>
          <Route path="/partner" element={<Partner token={token} setToken={setToken} />} />
          <Route path="/" element={<Navigate to="/video" replace />} />
          <Route
            path="/*"
            element={
              <AppLayout
                token={token}
                user={user}
                setUser={setUser}
                setSignedIn={setSignedIn}
                setToken={setToken}
                showLoginPopup={showLoginPopup}
                setShowLoginPopup={setShowLoginPopup}
              >
                <Routes>
                  <Route path="/login" element={<Login setToken={setToken} setUser={setUser} setSignedIn={setSignedIn} />} />
                  <Route path="/main" element={<PhotoGeneration token={token} user={user} setSignedIn={setSignedIn} setShowLoginPopup={setShowLoginPopup} />} />
                  <Route path="/video" element={<VideoGeneration token={token} user={user} setShowLoginPopup={setShowLoginPopup} />} />
                  <Route path="/video_template" element={<VideoTemplate token={token} user={user} setSignedIn={setSignedIn} setShowLoginPopup={setShowLoginPopup} />} />
                  <Route path="/gallery" element={<Gallery token={token} user={user} setUser={setUser} setSignedIn={setSignedIn} />} />
                  <Route path="/pricing" element={<Pricing token={token} />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/take_it_down_report" element={<TakeItDownReport />} />
                </Routes>
              </AppLayout>
            }
          />
        </Routes>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
